import React, { useState } from 'react';

const AddRoomModal = ({ show, onClose, onCreateRooms, property }) => {
  const [roomCount, setRoomCount] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const handleCreate = async () => {
    const count = parseInt(roomCount) || 1;

    if (!property?.id) {
      setError('Property information is missing');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const agencyId = sessionStorage.getItem('agencyId');
      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');

      if (!agencyId) {
        throw new Error('No agency ID found in session storage');
      }

      if (!token) {
        throw new Error('No authentication token found');
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const url = `${baseUrl}/agencies/${agencyId}/properties/${property.id}/rental-listings/batch-create`;

      const requestPayload = {
        number_of_listings: count
      };

      console.log('Creating rooms with payload:', requestPayload);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestPayload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create rooms: ${response.statusText}`);
      }

      const responseData = await response.json();
      console.log('Rooms created successfully:', responseData);

      // Call the callback to notify parent component with the correct response structure
      if (onCreateRooms) {
        // The API now returns 'created_listings' instead of 'listings'
        const formattedResponse = {
          listings: responseData.created_listings || [],
          count: responseData.count,
          message: responseData.message
        };
        onCreateRooms(count, formattedResponse);
      }

      // Reset form but keep modal open to show results
      setRoomCount('');
      setError(null);

      // Show success message
      alert(`Successfully created ${responseData.count} room(s)!`);

    } catch (error) {
      console.error('Error creating rooms:', error);
      setError(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    setRoomCount('');
    setError(null);
    onClose();
  };

  if (!show) return null;

  const count = parseInt(roomCount) || 0;
  const buttonText = count > 1 ? 'Create Rooms' : 'Create Room';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h5 className="text-xl font-semibold text-gray-900">Add Room(s)</h5>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            onClick={handleClose}
            disabled={isCreating}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="roomCount" className="block text-sm font-medium text-gray-700 mb-2">
              Number of rooms to create
            </label>
            <select
              id="roomCount"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors duration-200"
              value={roomCount}
              onChange={(e) => setRoomCount(e.target.value)}
              disabled={isCreating}
            >
              <option value="">Select number of rooms...</option>
              {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                <option key={num} value={num}>
                  {num} {num === 1 ? 'Room' : 'Rooms'}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            onClick={handleCreate}
            disabled={!roomCount || isCreating}
          >
            {isCreating ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              buttonText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddRoomModal;
